import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { format, min, sub } from 'date-fns';
import { PointOptionsObject } from 'highcharts';
import { EarningsPerDay } from '../../types';

@Component({
  selector: 'lohi-loads-chart',
  templateUrl: './loads-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadsChartComponent implements OnInit {
  public clonedEarnings: EarningsPerDay[];
  @Input() public earningsPerDay: EarningsPerDay[];
  @Input() public carrier = false;

  public get format(): string {
    return this.carrier ? DATE_FORMAT : DATETIME_FORMAT;
  }

  public get labels(): Array<string> {
    if (!this.earningsPerDay) {
      return [];
    }
    this.clonedEarnings = [...this.earningsPerDay];
    if (this.clonedEarnings.length < 10) {
      let minDate = min(this.clonedEarnings.map((value) => new Date(value.date)));
      while (this.clonedEarnings.length < 10) {
        minDate = sub(minDate, { days: 1 });
        this.clonedEarnings.push({
          date: minDate.toISOString(),
          lostEarnings: 0,
          earnings: 0,
          duration: 0,
          loadsLost: 0,
          loadsCompleted: 0,
          endedShiftEarly: false,
          endedShiftEarlyMinutes: 0,
          excessDeadhead: 0,
          excessLineHaul: 0,
          excessMineTime: 0,
          excessWellTime: 0,
          timeToAccept: 0,
          rejectedMinutes: 0,
          flickedMinutes: 0,
        });
      }
    }
    return this.carrier
      ? this.clonedEarnings?.slice().map((value) => {
          return format(new Date(value.date), this.format, {
            useAdditionalDayOfYearTokens: true,
            useAdditionalWeekYearTokens: true,
          });
        })
      : this.clonedEarnings
          ?.slice()
          .reverse()
          .map((value) => {
            return format(new Date(value.date), this.format, {
              useAdditionalDayOfYearTokens: true,
              useAdditionalWeekYearTokens: true,
            });
          });
  }

  /*public get target(): number {
    return this.driverTarget;
  }*/

  public chart: Chart;

  constructor() {}

  public ngOnInit() {
    const initialCount = this.earningsPerDay?.length || 10;
    const initialCountString = `${initialCount} Shift${initialCount !== 1 ? 's' : ''}`;
    const labels = this.labels;
    const title = this.carrier
      ? '<div class="text-lg font-bold">Carrier Loads <span class="text-blue-500">Past 14d</span></div>'
      : `<div class="text-lg font-bold">Driver Loads <span class="text-blue-500">Past ${initialCountString}</span></div>`;
    this.chart = new Chart({
      chart: {
        type: 'column',
      },
      title: {
        align: 'left',
        useHTML: true,
        text: title,
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          labels: {
            enabled: false,
          },
          tickInterval: 0,
          title: { text: null },
        },
        {
          title: { text: 'Loads' },
        },
      ],
      plotOptions: {
        series: {},
      },
      xAxis: {
        type: 'category',
        categories: labels,
        visible: true,
      },
      series: [],
    });

    const s3: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice().reverse()) {
      s3.push({
        label: format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        y: date.loadsCompleted,
      });
    }
    this.chart.addSeries(
      {
        name: 'Loads Completed',
        type: 'column',
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, '#070707'],
            [1, '#3e3e52'],
          ],
        },
        data: s3,
        yAxis: 1,
      },
      true,
      true,
    );
    const s4: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice().reverse()) {
      s4.push({
        label: format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        y: date.loadsLost as number,
        custom: {
          earning: date,
        },
      });
    }
    this.chart.addSeries(
      {
        name: 'Loads Lost',
        type: 'column',
        color: '#EF6048',
        data: s4,
        tooltip: {
          pointFormatter: pointFormat,
        },
        yAxis: 1,
      },
      true,
      true,
    );
  }
}

function pointFormat() {
  let main = `${this.series.name}: <b>${this.y}</b>`;
  if (this.custom.earning.rejectedMinutes > 0) {
    main += `<br>Rejecting Loads: <b>${formatDuration(this.custom.earning.rejectedMinutes)}</b>`;
  }
  if (this.custom.earning.flickedMinutes > 0) {
    main += `<br>Didn't Respond to Loads: <b>${formatDuration(this.custom.earning.flickedMinutes)}</b>`;
  }
  if (this.custom.earning.timeToAccept > 0) {
    main += `<br>Delayed Accepting Loads: <b>${formatDuration(this.custom.earning.timeToAccept)}</b>`;
  }
  if (this.custom.earning.excessDeadhead > 0) {
    main += `<br>Excess Deadhead: <b>${formatDuration(this.custom.earning.excessDeadhead)}</b>`;
  }
  if (this.custom.earning.excessMineTime > 0) {
    main += `<br>Excess Load-Out Wait: <b>${formatDuration(this.custom.earning.excessMineTime)}</b>`;
  }
  if (this.custom.earning.excessLineHaul > 0) {
    main += `<br>Excess Line Haul: <b>${formatDuration(this.custom.earning.excessLineHaul)}</b>`;
  }
  if (this.custom.earning.excessWellTime > 0) {
    main += `<br>Excess Delivery Wait: <b>${formatDuration(this.custom.earning.excessWellTime)}</b>`;
  }
  if (this.custom.earning.endedShiftEarlyMinutes > 0) {
    main += `<br>Ended Shift Early: <b>${formatDuration(this.custom.earning.endedShiftEarlyMinutes)}</b>`;
  }
  if (this.custom.earning.totalTimeLost > 0) {
    main += `<br><b>Total Lost Time: ${formatDuration(this.custom.earning.totalTimeLost)}</b>`;
  }
  return main;
}

function formatDuration(minutes: number) {
  return `${minutes > 60 ? Math.round(minutes / 60) + 'h' : ''} ${
    minutes % 60 > 0 ? Math.round(minutes % 60) + 'm' : ''
  }`;
}

const DATETIME_FORMAT = 'M/dd/yyyy hh:mm';
const DATE_FORMAT = 'M/dd/yyyy';
